/**
 * WizardQuestion Component
 * Renders different question types for the Model Planner Wizard
 * Handles: boolean, count, text, elevation, sheet_number, image, choice, manager_decision, discrepancy, info
 */

import React, { useState, useEffect, useRef } from 'react';
import { Trash2 } from 'lucide-react';
import ImagePasteArea from './ImagePasteArea';
import LabeledImageUpload from './LabeledImageUpload';
import GroupedFormQuestion from './GroupedFormQuestion';
import PondConfirmation from './PondConfirmation';
import WallConfirmation from './WallConfirmation';
import GradingConfirmation from './GradingConfirmation';
import DitchesConfirmation from './DitchesConfirmation';
import SidewalksConfirmation from './SidewalksConfirmation';
import AdaRampConfirmation from './AdaRampConfirmation';
import BuildingPadsConfirmation from './BuildingPadsConfirmation';
import GrateElevationsConfirmation from './GrateElevationsConfirmation';
import CurbConfirmation from './CurbConfirmation';
import WizardEngine from '../../utils/wizardEngine';

const WizardQuestion = ({
  question,
  currentData,
  onAnswer,
  onBack,
  canGoBack = false,
  projectId,
  clientId,
  onCreateDiscrepancy,
  sessionData,
  onItemClick,
  currentSection,
  onEditSingleQuestion,
  onAddAnotherItem,
  onDeleteItem
}) => {
  const [localValue, setLocalValue] = useState('');
  const [showWarning, setShowWarning] = useState(false);
  const inputRef = useRef(null);
  const wizardEngine = useRef(new WizardEngine()).current;

  // Load existing value if present
  useEffect(() => {
    if (question.field && currentData && currentData[question.field] !== undefined) {
      const value = currentData[question.field];
      // Handle different data types
      if (value !== null && value !== undefined) {
        if (typeof value === 'boolean') {
          // Boolean values don't need local state
        } else if (Array.isArray(value)) {
          // Arrays (like images) handled separately
        } else {
          setLocalValue(value.toString());
        }
      }
    }
  }, [question, currentData]);

  // Auto-focus text inputs when question changes
  useEffect(() => {
    if (inputRef.current && (question.type === 'text' || question.type === 'count' || question.type === 'elevation')) {
      inputRef.current.focus();
    }
  }, [question]);

  const handleBooleanAnswer = (answer) => {
    if (question.field) {
      // Check if the answer is changing from previous value
      const previousAnswer = currentData[question.field];
      const isChanging = previousAnswer !== undefined && previousAnswer !== answer;

      if (isChanging) {
        // Answer is changing - we need to clear stale fields from the abandoned path
        let fieldsToClear = [];

        if (previousAnswer === true && answer === false) {
          // Changing from Yes to No - clear fields from onYes path
          if (question.onYes && currentSection) {
            fieldsToClear = wizardEngine.getFieldsInPath(currentSection, question.onYes, question.next || question.onNo);
            console.log(`[handleBooleanAnswer] Clearing ${fieldsToClear.length} fields from onYes path:`, fieldsToClear);
          }
        } else if (previousAnswer === false && answer === true) {
          // Changing from No to Yes - clear fields from onNo path
          if (question.onNo && currentSection) {
            fieldsToClear = wizardEngine.getFieldsInPath(currentSection, question.onNo, question.next || question.onYes);
            console.log(`[handleBooleanAnswer] Clearing ${fieldsToClear.length} fields from onNo path:`, fieldsToClear);
          }
        }

        // Clear the fields by setting them to null
        fieldsToClear.forEach(field => {
          onAnswer(field, null);
        });
      }

      // Save the new answer
      onAnswer(question.field, answer);
    }

    // Navigate based on answer - pass answer for history
    setTimeout(() => {
      if (answer && question.onYes) {
        onAnswer('_navigate', question.onYes, answer);
      } else if (!answer && question.onNo) {
        onAnswer('_navigate', question.onNo, answer);
      } else if (question.next) {
        onAnswer('_navigate', question.next, answer);
      }
    }, 50);
  };

  const handleTextSubmit = () => {
    if (!localValue.trim() && question.type !== 'count') {
      setShowWarning(true);
      // Still allow proceeding after warning
      setTimeout(() => setShowWarning(false), 2000);
    }

    if (question.field) {
      onAnswer(question.field, localValue);
    }

    // Delay navigation - pass answer for history
    setTimeout(() => {
      if (question.next) {
        onAnswer('_navigate', question.next, localValue);
      }
    }, 50);
  };

  const handleCountSubmit = () => {
    console.log('[WizardQuestion:handleCountSubmit] ===== COUNT SUBMIT =====');
    console.log('[WizardQuestion:handleCountSubmit] localValue:', localValue);
    console.log('[WizardQuestion:handleCountSubmit] question.id:', question.id);
    console.log('[WizardQuestion:handleCountSubmit] question.next:', question.next);

    const count = parseInt(localValue);
    console.log('[WizardQuestion:handleCountSubmit] parsed count:', count);

    if (isNaN(count) || count < 1) {
      console.log('[WizardQuestion:handleCountSubmit] Validation FAILED');
      alert('Please enter a valid number (1 or greater)');
      return;
    }
    console.log('[WizardQuestion:handleCountSubmit] Validation PASSED');

    // Store count and initiate loop
    console.log('[WizardQuestion:handleCountSubmit] Calling onAnswer("_count", ' + count + ')');
    onAnswer('_count', count);

    if (question.field) {
      console.log('[WizardQuestion:handleCountSubmit] Calling onAnswer("' + question.field + '", ' + count + ')');
      onAnswer(question.field, count);
    } else {
      console.log('[WizardQuestion:handleCountSubmit] No question.field, skipping field storage');
    }

    // Navigate immediately with count value passed directly
    if (question.next) {
      console.log('[WizardQuestion:handleCountSubmit] About to navigate to:', question.next, 'with count:', count);
      onAnswer('_navigate', question.next, count);
      console.log('[WizardQuestion:handleCountSubmit] Navigation call completed');
    }
  };

  const handleElevationSubmit = (useNA = false) => {
    const answerValue = useNA ? 'N/A' : localValue;

    if (useNA && question.naField) {
      onAnswer(question.naField, true);
      onAnswer(question.field, null);
    } else {
      if (question.naField) {
        onAnswer(question.naField, false);
      }
      onAnswer(question.field, localValue);
    }

    // Delay navigation - pass answer for history
    setTimeout(() => {
      if (question.next) {
        onAnswer('_navigate', question.next, answerValue);
      }
    }, 50);
  };

  const handleImageChange = (images) => {
    if (question.field) {
      onAnswer(question.field, images);
    }
  };

  const handleImageSubmit = (images) => {
    // Images are already saved via handleImageChange
    // Delay navigation - pass images for history
    setTimeout(() => {
      if (question.next) {
        onAnswer('_navigate', question.next, images);
      }
    }, 50);
  };

  const handleChoiceSelect = (choice) => {
    if (question.field) {
      onAnswer(question.field, choice);
    }

    // Delay navigation - pass choice for history
    setTimeout(() => {
      if (question.next) {
        onAnswer('_navigate', question.next, choice);
      }
    }, 50);
  };

  const handleMultiCheckboxToggle = (choice) => {
    const currentValue = currentData?.[question.field] || [];
    const newValue = currentValue.includes(choice)
      ? currentValue.filter(c => c !== choice)
      : [...currentValue, choice];

    if (question.field) {
      onAnswer(question.field, newValue);
    }
  };

  const handleMultiCheckboxSubmit = () => {
    const selectedValues = currentData?.[question.field] || [];

    // Delay navigation - pass selected values for history
    setTimeout(() => {
      if (question.next) {
        onAnswer('_navigate', question.next, selectedValues);
      }
    }, 50);
  };

  const handleDiscrepancyCreate = () => {
    if (onCreateDiscrepancy) {
      onCreateDiscrepancy();
    }

    // After discrepancy is created (modal closes), navigate
    if (question.next) {
      onAnswer('_navigate', question.next);
    }
  };

  const handleInfoAcknowledge = () => {
    // Info questions just need acknowledgment
    if (question.field) {
      onAnswer(question.field, true);
    }

    // Delay navigation - pass true for history
    setTimeout(() => {
      if (question.next) {
        onAnswer('_navigate', question.next, true);
      }
    }, 50);
  };

  const handleConfirmationEdit = (questionId) => {
    console.log('[handleConfirmationEdit] ===== EDIT BUTTON HANDLER TRIGGERED =====');
    console.log('[handleConfirmationEdit] Called with questionId:', questionId);
    console.log('[handleConfirmationEdit] onEditSingleQuestion exists?', !!onEditSingleQuestion);
    console.log('[handleConfirmationEdit] onEditSingleQuestion function:', onEditSingleQuestion);
    console.log('[handleConfirmationEdit] currentSection:', currentSection);
    console.log('[handleConfirmationEdit] currentData:', currentData);
    console.log('[handleConfirmationEdit] question:', question);

    // Activate single question edit mode if callback provided
    if (onEditSingleQuestion) {
      console.log('[handleConfirmationEdit] About to call onEditSingleQuestion with:', questionId);
      onEditSingleQuestion(questionId);
      console.log('[handleConfirmationEdit] Completed calling onEditSingleQuestion');
    } else {
      console.log('[handleConfirmationEdit] WARNING: onEditSingleQuestion is not defined!');
    }
    // Delay navigation slightly to allow state update to process
    setTimeout(() => {
      console.log('[handleConfirmationEdit] About to call onAnswer with _navigate to:', questionId);
      onAnswer('_navigate', questionId);
      console.log('[handleConfirmationEdit] Completed calling onAnswer');
    }, 10);
  };

  const handleConfirmationComplete = async () => {
    console.log('[handleConfirmationComplete] ===== CONFIRM & CONTINUE BUTTON CLICKED =====');
    console.log('[handleConfirmationComplete] question:', question);
    console.log('[handleConfirmationComplete] question.next:', question.next);
    console.log('[handleConfirmationComplete] About to call onAnswer("_markComplete", true)');

    // Mark current item as completed - WAIT for it to finish
    await onAnswer('_markComplete', true);

    console.log('[handleConfirmationComplete] Completed onAnswer("_markComplete", true)');

    // Proceed to LOOP_END (next pond or section complete) - WAIT for it to finish
    if (question.next) {
      console.log('[handleConfirmationComplete] About to call onAnswer("_navigate", question.next)');
      await onAnswer('_navigate', question.next);
      console.log('[handleConfirmationComplete] Completed onAnswer("_navigate", question.next)');
    } else {
      console.log('[handleConfirmationComplete] No question.next - not navigating');
    }
  };

  // Helper to get section data
  const getSectionData = (sectionKey) => {
    const keyMap = {
      'ponds': 'ponds',
      'curb': 'curbTypes',
      'grading': 'gradingSections',
      'walls': 'walls',
      'ditches': 'ditchesSwales',
      'sidewalks': 'sidewalks',
      'ada-ramps': 'adaRamps',
      'building-pads': 'buildingPads',
      'grate-elevations': 'grateElevations'
    };
    const dataKey = keyMap[sectionKey] || sectionKey;
    const data = sessionData?.[dataKey];

    // For single sections (grading, ditches, sidewalks, building-pads, grate-elevations),
    // wrap the object in an array so it can be mapped
    if (data && !Array.isArray(data)) {
      return [data];
    }

    return data || [];
  };

  // Helper to get section item label
  const getSectionItemLabel = (sectionKey) => {
    const labelMap = {
      'ponds': 'Pond',
      'curb': 'Curb Type',
      'walls': 'Wall',
      'ada-ramps': 'ADA Ramp',
      'grading': 'Section',
      'ditches': 'Item',
      'sidewalks': 'Item',
      'building-pads': 'Item',
      'grate-elevations': 'Item'
    };
    return labelMap[sectionKey] || 'Item';
  };

  // Render based on question type
  switch (question.type) {
    case 'section_summary':
      const items = getSectionData(question.sectionKey);

      // Select appropriate confirmation component based on section
      const getConfirmationComponent = (sectionKey, item, index) => {
        const commonProps = {
          currentData: item,
          onEdit: (questionId) => {
            onItemClick && onItemClick(sectionKey, item, index, questionId);
          },
          onConfirm: () => {
            // No confirm action needed in summary view
          },
          canGoBack: false,
          isCompleted: true  // Always true in summary view - hide Confirm & Continue button
        };

        switch (sectionKey) {
          case 'curb':
            return <CurbConfirmation {...commonProps} />;
          case 'walls':
            return <WallConfirmation {...commonProps} />;
          case 'grading':
            return <GradingConfirmation {...commonProps} />;
          case 'ditches':
            return <DitchesConfirmation {...commonProps} />;
          case 'sidewalks':
            return <SidewalksConfirmation {...commonProps} sessionData={sessionData} onItemClick={onItemClick} onAddAnotherItem={onAddAnotherItem} onDataUpdate={onAnswer} projectId={projectId} clientId={clientId} onDeleteItem={onDeleteItem} />;
          case 'building-pads':
            return <BuildingPadsConfirmation {...commonProps} />;
          case 'grate-elevations':
            return <GrateElevationsConfirmation {...commonProps} />;
          case 'ponds':
          default:
            return <PondConfirmation {...commonProps} />;
        }
      };

      // Determine if this is a single section
      const sectionConfig = wizardEngine.getSection(question.sectionKey);
      const isSingleSection = sectionConfig?.type === 'single';

      return (
        <div className="section-summary-container">
          {/* Header with Add Button */}
          <div style={{
            marginBottom: 'var(--space-6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--space-4)'
          }}>
            <h2 style={{
              margin: 0,
              fontSize: 'var(--text-2xl)',
              fontWeight: 'var(--font-bold)',
              color: 'var(--gray-50)'
            }}>{question.text}</h2>

            {/* Add Another Item button - only for looped sections */}
            {onAddAnotherItem && ['ponds', 'curb', 'walls', 'ada-ramps'].includes(question.sectionKey) && (
              <button
                type="button"
                onClick={() => onAddAnotherItem(question.sectionKey)}
                style={{
                  padding: 'var(--space-3) var(--space-6)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-semibold)',
                  border: 'none',
                  backgroundColor: 'var(--cta-orange)',
                  color: 'white',
                  cursor: 'pointer',
                  borderRadius: 'var(--radius-md)',
                  transition: 'all var(--transition-normal)',
                  boxShadow: 'var(--shadow-sm)',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--cta-orange-hover)';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = 'var(--shadow-md)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--cta-orange)';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'var(--shadow-sm)';
                }}
              >
                + New {getSectionItemLabel(question.sectionKey)}
              </button>
            )}
          </div>

          {items.length === 0 ? (
            <div style={{
              padding: 'var(--space-10)',
              textAlign: 'center',
              backgroundColor: 'var(--dark-surface)',
              border: '1px solid var(--gray-700)',
              borderRadius: 'var(--radius-lg)'
            }}>
              <p style={{
                margin: 0,
                color: 'var(--gray-400)',
                fontSize: 'var(--text-base)'
              }}>
                No items added yet
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
              {items.map((item, index) => (
                <div
                  key={item.id || index}
                  style={{
                    border: '2px solid var(--gray-700)',
                    borderRadius: 'var(--radius-lg)',
                    overflow: 'hidden',
                    backgroundColor: 'var(--dark-surface)',
                    position: 'relative'
                  }}
                >
                  {/* Delete button - top right corner (all sections) */}
                  {onDeleteItem && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isSingleSection) {
                          // For single sections, pass section name and type
                          const sectionName = sectionConfig.label;
                          onDeleteItem(question.sectionKey, null, sectionName, 'section');
                        } else {
                          // For looped sections, pass item name and type
                          const itemName = item.pond_name || item.curb_type_name || item.wall_name_location || item.ramp_type || `Item ${index + 1}`;
                          const itemType = question.sectionKey === 'ponds' ? 'pond' :
                                          question.sectionKey === 'curb' ? 'curb type' :
                                          question.sectionKey === 'walls' ? 'wall' :
                                          question.sectionKey === 'ada-ramps' ? 'ADA ramp' : 'item';
                          onDeleteItem(question.sectionKey, item.id, itemName, itemType);
                        }
                      }}
                      style={{
                        position: 'absolute',
                        top: 'var(--space-4)',
                        right: 'var(--space-4)',
                        padding: 'var(--space-2)',
                        border: '1px solid var(--gray-600)',
                        backgroundColor: 'var(--dark-surface)',
                        color: 'var(--gray-400)',
                        cursor: 'pointer',
                        borderRadius: 'var(--radius-md)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all var(--transition-fast)',
                        zIndex: 10
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#dc2626';
                        e.target.style.borderColor = '#dc2626';
                        e.target.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'var(--dark-surface)';
                        e.target.style.borderColor = 'var(--gray-600)';
                        e.target.style.color = 'var(--gray-400)';
                      }}
                      title={isSingleSection ? "Clear this section" : "Delete this item"}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                  {getConfirmationComponent(question.sectionKey, item, index)}
                </div>
              ))}
            </div>
          )}
        </div>
      );

    case 'confirmation':
      // Render section-specific confirmation component based on currentSection
      if (currentSection === 'ponds' || question.id === 'pond_confirmation') {
        return (
          <PondConfirmation
            currentData={currentData}
            onEdit={handleConfirmationEdit}
            onConfirm={handleConfirmationComplete}
            onBack={onBack}
            canGoBack={canGoBack}
            isCompleted={false}  // Always show button during wizard flow
            onDataUpdate={onAnswer}
            projectId={projectId}
            clientId={clientId}
          />
        );
      } else if (currentSection === 'walls') {
        return (
          <WallConfirmation
            currentData={currentData}
            onEdit={handleConfirmationEdit}
            onConfirm={handleConfirmationComplete}
            onBack={onBack}
            canGoBack={canGoBack}
            isCompleted={false}  // Always show button during wizard flow
            onDataUpdate={onAnswer}
            projectId={projectId}
            clientId={clientId}
          />
        );
      } else if (currentSection === 'grading') {
        return (
          <GradingConfirmation
            currentData={currentData}
            onEdit={handleConfirmationEdit}
            onConfirm={handleConfirmationComplete}
            onBack={onBack}
            canGoBack={canGoBack}
            isCompleted={false}  // Always show button during wizard flow
            onDataUpdate={onAnswer}
            projectId={projectId}
            clientId={clientId}
          />
        );
      } else if (currentSection === 'ditches') {
        return (
          <DitchesConfirmation
            currentData={currentData}
            onEdit={handleConfirmationEdit}
            onConfirm={handleConfirmationComplete}
            onBack={onBack}
            canGoBack={canGoBack}
            isCompleted={false}  // Always show button during wizard flow
            onDataUpdate={onAnswer}
            projectId={projectId}
            clientId={clientId}
          />
        );
      } else if (currentSection === 'sidewalks' && question.id === 'sidewalks_confirmation') {
        return (
          <SidewalksConfirmation
            currentData={currentData}
            onEdit={handleConfirmationEdit}
            onConfirm={handleConfirmationComplete}
            onBack={onBack}
            canGoBack={canGoBack}
            isCompleted={false}  // Always show button during wizard flow
            sessionData={sessionData}
            onItemClick={onItemClick}
            onAddAnotherItem={onAddAnotherItem}
            onDataUpdate={onAnswer}
            projectId={projectId}
            clientId={clientId}
            onDelete={() => {
              onDeleteItem && onDeleteItem('sidewalks', null, 'Sidewalks', 'section');
            }}
            onDeleteItem={onDeleteItem}
          />
        );
      } else if (question.id === 'ada_ramp_confirmation') {
        return (
          <AdaRampConfirmation
            currentData={currentData}
            onEdit={handleConfirmationEdit}
            onConfirm={handleConfirmationComplete}
            onBack={onBack}
            canGoBack={canGoBack}
            isCompleted={false}  // Always show button during wizard flow
            onDataUpdate={onAnswer}
            projectId={projectId}
            clientId={clientId}
          />
        );
      } else if (currentSection === 'building-pads') {
        console.log('[WizardQuestion] Rendering BuildingPadsConfirmation');
        console.log('[WizardQuestion] question:', question);
        console.log('[WizardQuestion] question.type:', question.type);
        console.log('[WizardQuestion] question.id:', question.id);
        console.log('[WizardQuestion] currentData:', currentData);
        console.log('[WizardQuestion] Passing onConfirm={handleConfirmationComplete}');
        return (
          <BuildingPadsConfirmation
            currentData={currentData}
            onEdit={handleConfirmationEdit}
            onConfirm={handleConfirmationComplete}
            onBack={onBack}
            canGoBack={canGoBack}
            isCompleted={false}  // Always show button during wizard flow
            onDataUpdate={onAnswer}
            projectId={projectId}
            clientId={clientId}
          />
        );
      } else if (currentSection === 'grate-elevations') {
        return (
          <GrateElevationsConfirmation
            currentData={currentData}
            onEdit={handleConfirmationEdit}
            onConfirm={handleConfirmationComplete}
            onBack={onBack}
            canGoBack={canGoBack}
            isCompleted={false}  // Always show button during wizard flow
            onDataUpdate={onAnswer}
            projectId={projectId}
            clientId={clientId}
          />
        );
      } else if (currentSection === 'curb') {
        return (
          <CurbConfirmation
            currentData={currentData}
            onEdit={handleConfirmationEdit}
            onConfirm={handleConfirmationComplete}
            onBack={onBack}
            canGoBack={canGoBack}
            isCompleted={false}  // Always show button during wizard flow
            onDataUpdate={onAnswer}
            projectId={projectId}
            clientId={clientId}
          />
        );
      } else {
        // Fallback to PondConfirmation for now (will add more sections later)
        return (
          <PondConfirmation
            currentData={currentData}
            onEdit={handleConfirmationEdit}
            onConfirm={handleConfirmationComplete}
            onBack={onBack}
            canGoBack={canGoBack}
            isCompleted={false}  // Always show button during wizard flow
            onDataUpdate={onAnswer}
            projectId={projectId}
            clientId={clientId}
          />
        );
      }

    case 'grouped_form':
      return (
        <GroupedFormQuestion
          question={question}
          currentData={currentData}
          onAnswer={onAnswer}
          onBack={onBack}
          canGoBack={canGoBack}
          projectId={projectId}
          clientId={clientId}
          currentSection={currentSection}
        />
      );

    case 'boolean':
      return (
        <div className="wizard-question-container">
          <h3 style={{
            marginBottom: 'var(--space-5)',
            fontSize: 'var(--text-xl)',
            fontWeight: 'var(--font-semibold)',
            color: 'var(--gray-50)'
          }}>{question.text}</h3>
          <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'center' }}>
            {canGoBack && (
              <button
                onClick={onBack}
                style={{
                  padding: 'var(--space-3) var(--space-8)',
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-medium)',
                  border: '1px solid var(--gray-600)',
                  backgroundColor: 'var(--dark-elevated)',
                  color: 'var(--gray-300)',
                  cursor: 'pointer',
                  borderRadius: 'var(--radius-md)',
                  transition: 'all var(--transition-fast)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--gray-700)';
                  e.target.style.borderColor = 'var(--gray-500)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--dark-elevated)';
                  e.target.style.borderColor = 'var(--gray-600)';
                }}
              >
                ← Back
              </button>
            )}
            <button
              onClick={() => handleBooleanAnswer(true)}
              style={{
                padding: 'var(--space-3) var(--space-8)',
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-semibold)',
                border: '1px solid var(--cta-blue)',
                backgroundColor: 'var(--cta-blue)',
                color: 'white',
                cursor: 'pointer',
                borderRadius: 'var(--radius-md)',
                transition: 'all var(--transition-fast)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#2563eb';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'var(--cta-blue)';
              }}
            >
              Yes
            </button>
            <button
              onClick={() => handleBooleanAnswer(false)}
              style={{
                padding: 'var(--space-3) var(--space-8)',
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-semibold)',
                border: '1px solid var(--gray-600)',
                backgroundColor: 'var(--dark-elevated)',
                color: 'var(--gray-200)',
                cursor: 'pointer',
                borderRadius: 'var(--radius-md)',
                transition: 'all var(--transition-fast)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'var(--gray-700)';
                e.target.style.borderColor = 'var(--gray-500)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'var(--dark-elevated)';
                e.target.style.borderColor = 'var(--gray-600)';
              }}
            >
              No
            </button>
          </div>
        </div>
      );

    case 'count':
      return (
        <div className="wizard-question-container">
          <h3 style={{
            marginBottom: 'var(--space-5)',
            fontSize: 'var(--text-xl)',
            fontWeight: 'var(--font-semibold)',
            color: 'var(--gray-50)'
          }}>{question.text}</h3>
          <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
            {canGoBack && (
              <button
                onClick={onBack}
                style={{
                  padding: 'var(--space-3) var(--space-6)',
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-medium)',
                  border: '1px solid var(--gray-600)',
                  backgroundColor: 'var(--dark-elevated)',
                  color: 'var(--gray-300)',
                  cursor: 'pointer',
                  borderRadius: 'var(--radius-md)',
                  transition: 'all var(--transition-fast)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--gray-700)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--dark-elevated)';
                }}
              >
                ← Back
              </button>
            )}
            <input
              ref={inputRef}
              type="number"
              min="1"
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleCountSubmit();
                }
              }}
              placeholder="Enter number..."
              style={{
                padding: 'var(--space-3)',
                fontSize: 'var(--text-base)',
                border: '1px solid var(--gray-600)',
                backgroundColor: 'var(--dark-elevated)',
                color: 'var(--gray-50)',
                borderRadius: 'var(--radius-md)',
                width: '200px'
              }}
            />
            <button
              onClick={handleCountSubmit}
              style={{
                padding: 'var(--space-3) var(--space-6)',
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-semibold)',
                border: '1px solid var(--cta-blue)',
                backgroundColor: 'var(--cta-blue)',
                color: 'white',
                cursor: 'pointer',
                borderRadius: 'var(--radius-md)',
                transition: 'all var(--transition-fast)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#2563eb';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'var(--cta-blue)';
              }}
            >
              Continue
            </button>
          </div>
        </div>
      );

    case 'text':
    case 'sheet_number':
      return (
        <div className="wizard-question-container">
          <h3 style={{
            marginBottom: 'var(--space-5)',
            fontSize: 'var(--text-xl)',
            fontWeight: 'var(--font-semibold)',
            color: 'var(--gray-50)'
          }}>{question.text}</h3>
          {showWarning && (
            <div style={{
              padding: 'var(--space-3)',
              backgroundColor: 'rgba(245, 158, 11, 0.15)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--space-3)',
              color: '#fbbf24'
            }}>
              Warning: Field is empty. Are you sure you want to continue?
            </div>
          )}
          <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
            {canGoBack && (
              <button
                onClick={onBack}
                style={{
                  padding: 'var(--space-3) var(--space-6)',
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-medium)',
                  border: '1px solid var(--gray-600)',
                  backgroundColor: 'var(--dark-elevated)',
                  color: 'var(--gray-300)',
                  cursor: 'pointer',
                  borderRadius: 'var(--radius-md)',
                  transition: 'all var(--transition-fast)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--gray-700)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--dark-elevated)';
                }}
              >
                ← Back
              </button>
            )}
            <input
              ref={inputRef}
              type="text"
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleTextSubmit();
                }
              }}
              placeholder={question.placeholder || 'Enter text...'}
              style={{
                padding: 'var(--space-3)',
                fontSize: 'var(--text-base)',
                border: '1px solid var(--gray-600)',
                backgroundColor: 'var(--dark-elevated)',
                color: 'var(--gray-50)',
                borderRadius: 'var(--radius-md)',
                width: '400px'
              }}
            />
            <button
              onClick={handleTextSubmit}
              style={{
                padding: 'var(--space-3) var(--space-6)',
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-semibold)',
                border: '1px solid var(--cta-blue)',
                backgroundColor: 'var(--cta-blue)',
                color: 'white',
                cursor: 'pointer',
                borderRadius: 'var(--radius-md)',
                transition: 'all var(--transition-fast)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#2563eb';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'var(--cta-blue)';
              }}
            >
              Continue
            </button>
          </div>
        </div>
      );

    case 'elevation':
      return (
        <div className="wizard-question-container">
          <h3 style={{
            marginBottom: 'var(--space-5)',
            fontSize: 'var(--text-xl)',
            fontWeight: 'var(--font-semibold)',
            color: 'var(--gray-50)'
          }}>{question.text}</h3>
          {showWarning && (
            <div style={{
              padding: 'var(--space-3)',
              backgroundColor: 'rgba(245, 158, 11, 0.15)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--space-3)',
              color: '#fbbf24'
            }}>
              Warning: Elevation is empty. Are you sure you want to continue?
            </div>
          )}
          <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap' }}>
            {canGoBack && (
              <button
                onClick={onBack}
                style={{
                  padding: 'var(--space-3) var(--space-6)',
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-medium)',
                  border: '1px solid var(--gray-600)',
                  backgroundColor: 'var(--dark-elevated)',
                  color: 'var(--gray-300)',
                  cursor: 'pointer',
                  borderRadius: 'var(--radius-md)',
                  transition: 'all var(--transition-fast)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--gray-700)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--dark-elevated)';
                }}
              >
                ← Back
              </button>
            )}
            <input
              ref={inputRef}
              type="text"
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleElevationSubmit(false);
                }
              }}
              placeholder="Enter elevation..."
              style={{
                padding: 'var(--space-3)',
                fontSize: 'var(--text-base)',
                border: '1px solid var(--gray-600)',
                backgroundColor: 'var(--dark-elevated)',
                color: 'var(--gray-50)',
                borderRadius: 'var(--radius-md)',
                width: '200px'
              }}
            />
            <button
              onClick={() => handleElevationSubmit(false)}
              style={{
                padding: 'var(--space-3) var(--space-6)',
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-semibold)',
                border: '1px solid var(--cta-blue)',
                backgroundColor: 'var(--cta-blue)',
                color: 'white',
                cursor: 'pointer',
                borderRadius: 'var(--radius-md)',
                transition: 'all var(--transition-fast)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#2563eb';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'var(--cta-blue)';
              }}
            >
              Continue
            </button>
            {question.allowNA && (
              <button
                onClick={() => handleElevationSubmit(true)}
                style={{
                  padding: 'var(--space-3) var(--space-6)',
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-semibold)',
                  border: '1px solid var(--gray-600)',
                  backgroundColor: 'var(--dark-elevated)',
                  color: 'var(--gray-200)',
                  cursor: 'pointer',
                  borderRadius: 'var(--radius-md)',
                  transition: 'all var(--transition-fast)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--gray-700)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--dark-elevated)';
                }}
              >
                N/A
              </button>
            )}
          </div>
        </div>
      );

    case 'image':
      // Use labeled upload by default (can be disabled with useLabeledUpload: false)
      const ImageUploadComponent = question.useLabeledUpload === false ? ImagePasteArea : LabeledImageUpload;

      return (
        <div className="wizard-question-container">
          <h3 style={{
            marginBottom: 'var(--space-5)',
            fontSize: 'var(--text-xl)',
            fontWeight: 'var(--font-semibold)',
            color: 'var(--gray-50)'
          }}>{question.text}</h3>
          <ImageUploadComponent
            images={currentData?.[question.field] || []}
            onImagesChange={handleImageChange}
            projectId={projectId}
            clientId={clientId}
          />
          <div style={{ marginTop: 'var(--space-4)', display: 'flex', gap: 'var(--space-3)' }}>
            {canGoBack && (
              <button
                onClick={onBack}
                style={{
                  padding: 'var(--space-3) var(--space-6)',
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-medium)',
                  border: '1px solid var(--gray-600)',
                  backgroundColor: 'var(--dark-elevated)',
                  color: 'var(--gray-300)',
                  cursor: 'pointer',
                  borderRadius: 'var(--radius-md)',
                  transition: 'all var(--transition-fast)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--gray-700)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--dark-elevated)';
                }}
              >
                ← Back
              </button>
            )}
            <button
              onClick={() => handleImageSubmit(currentData?.[question.field] || [])}
              style={{
                padding: 'var(--space-3) var(--space-6)',
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-semibold)',
                border: '1px solid var(--cta-blue)',
                backgroundColor: 'var(--cta-blue)',
                color: 'white',
                cursor: 'pointer',
                borderRadius: 'var(--radius-md)',
                transition: 'all var(--transition-fast)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#2563eb';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'var(--cta-blue)';
              }}
            >
              Continue
            </button>
          </div>
        </div>
      );

    case 'choice':
      return (
        <div className="wizard-question-container">
          <h3 style={{
            marginBottom: 'var(--space-5)',
            fontSize: 'var(--text-xl)',
            fontWeight: 'var(--font-semibold)',
            color: 'var(--gray-50)'
          }}>{question.text}</h3>
          <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap', alignItems: 'center' }}>
            {canGoBack && (
              <button
                onClick={onBack}
                style={{
                  padding: 'var(--space-3) var(--space-8)',
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-medium)',
                  border: '1px solid var(--gray-600)',
                  backgroundColor: 'var(--dark-elevated)',
                  color: 'var(--gray-300)',
                  cursor: 'pointer',
                  borderRadius: 'var(--radius-md)',
                  transition: 'all var(--transition-fast)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--gray-700)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--dark-elevated)';
                }}
              >
                ← Back
              </button>
            )}
            {question.choices && question.choices.map((choice) => (
              <button
                key={choice}
                onClick={() => handleChoiceSelect(choice)}
                style={{
                  padding: 'var(--space-3) var(--space-6)',
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-semibold)',
                  border: '1px solid',
                  borderColor: currentData?.[question.field] === choice ? 'var(--cta-blue)' : 'var(--gray-600)',
                  backgroundColor: currentData?.[question.field] === choice ? 'var(--cta-blue)' : 'var(--dark-elevated)',
                  color: currentData?.[question.field] === choice ? 'white' : 'var(--gray-200)',
                  cursor: 'pointer',
                  borderRadius: 'var(--radius-md)',
                  transition: 'all var(--transition-fast)'
                }}
                onMouseEnter={(e) => {
                  if (currentData?.[question.field] !== choice) {
                    e.target.style.backgroundColor = 'var(--gray-700)';
                    e.target.style.borderColor = 'var(--gray-500)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentData?.[question.field] !== choice) {
                    e.target.style.backgroundColor = 'var(--dark-elevated)';
                    e.target.style.borderColor = 'var(--gray-600)';
                  }
                }}
              >
                {choice}
              </button>
            ))}
          </div>
        </div>
      );

    case 'multi_checkbox':
      const selectedValues = currentData?.[question.field] || [];
      return (
        <div className="wizard-question-container">
          <h3 style={{
            marginBottom: 'var(--space-5)',
            fontSize: 'var(--text-xl)',
            fontWeight: 'var(--font-semibold)',
            color: 'var(--gray-50)'
          }}>{question.text}</h3>
          <div style={{ marginBottom: 'var(--space-5)' }}>
            {question.choices && question.choices.map((choice) => {
              const isChecked = selectedValues.includes(choice);
              return (
                <div key={choice} style={{
                  marginBottom: 'var(--space-3)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)'
                }}>
                  <input
                    type="checkbox"
                    id={`checkbox-${choice}`}
                    checked={isChecked}
                    onChange={() => handleMultiCheckboxToggle(choice)}
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer',
                      accentColor: 'var(--cta-blue)'
                    }}
                  />
                  <label htmlFor={`checkbox-${choice}`} style={{
                    fontSize: 'var(--text-base)',
                    color: 'var(--gray-200)',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}>
                    {choice}
                  </label>
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
            {canGoBack && (
              <button
                onClick={onBack}
                style={{
                  padding: 'var(--space-3) var(--space-8)',
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-medium)',
                  border: '1px solid var(--gray-600)',
                  backgroundColor: 'var(--dark-elevated)',
                  color: 'var(--gray-300)',
                  cursor: 'pointer',
                  borderRadius: 'var(--radius-md)',
                  transition: 'all var(--transition-fast)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--gray-700)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--dark-elevated)';
                }}
              >
                ← Back
              </button>
            )}
            <button
              onClick={handleMultiCheckboxSubmit}
              style={{
                padding: 'var(--space-3) var(--space-8)',
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-bold)',
                border: '1px solid var(--cta-blue)',
                backgroundColor: 'var(--cta-blue)',
                color: 'white',
                cursor: 'pointer',
                borderRadius: 'var(--radius-md)',
                transition: 'all var(--transition-fast)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#2563eb';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'var(--cta-blue)';
              }}
            >
              Continue
            </button>
          </div>
        </div>
      );

    case 'manager_decision':
      return (
        <div className="wizard-question-container" style={{
          backgroundColor: 'rgba(245, 158, 11, 0.15)',
          border: '1px solid rgba(245, 158, 11, 0.3)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-5)'
        }}>
          <h3 style={{
            marginBottom: 'var(--space-3)',
            fontSize: 'var(--text-lg)',
            fontWeight: 'var(--font-bold)',
            color: '#fbbf24'
          }}>Manager Decision Required</h3>
          <p style={{
            marginBottom: 'var(--space-5)',
            fontSize: 'var(--text-base)',
            color: 'var(--gray-200)'
          }}>{question.text}</p>
          <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'center' }}>
            {canGoBack && (
              <button
                onClick={onBack}
                style={{
                  padding: 'var(--space-3) var(--space-8)',
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-medium)',
                  border: '1px solid var(--gray-600)',
                  backgroundColor: 'var(--dark-elevated)',
                  color: 'var(--gray-300)',
                  cursor: 'pointer',
                  borderRadius: 'var(--radius-md)',
                  transition: 'all var(--transition-fast)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--gray-700)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--dark-elevated)';
                }}
              >
                ← Back
              </button>
            )}
            <button
              onClick={() => handleBooleanAnswer(true)}
              style={{
                padding: 'var(--space-3) var(--space-8)',
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-semibold)',
                border: '1px solid #fbbf24',
                backgroundColor: '#fbbf24',
                color: '#1e1e1e',
                cursor: 'pointer',
                borderRadius: 'var(--radius-md)',
                transition: 'all var(--transition-fast)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f59e0b';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#fbbf24';
              }}
            >
              Yes
            </button>
            <button
              onClick={() => handleBooleanAnswer(false)}
              style={{
                padding: 'var(--space-3) var(--space-8)',
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-semibold)',
                border: '1px solid var(--gray-600)',
                backgroundColor: 'var(--dark-elevated)',
                color: 'var(--gray-200)',
                cursor: 'pointer',
                borderRadius: 'var(--radius-md)',
                transition: 'all var(--transition-fast)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'var(--gray-700)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'var(--dark-elevated)';
              }}
            >
              No
            </button>
          </div>
        </div>
      );

    case 'manager_choice':
      return (
        <div className="wizard-question-container" style={{
          backgroundColor: 'rgba(245, 158, 11, 0.15)',
          border: '1px solid rgba(245, 158, 11, 0.3)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-5)'
        }}>
          <h3 style={{
            marginBottom: 'var(--space-3)',
            fontSize: 'var(--text-lg)',
            fontWeight: 'var(--font-bold)',
            color: '#fbbf24'
          }}>Manager Decision Required</h3>
          <p style={{
            marginBottom: 'var(--space-5)',
            fontSize: 'var(--text-base)',
            color: 'var(--gray-200)'
          }}>{question.text}</p>
          <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap', alignItems: 'center' }}>
            {canGoBack && (
              <button
                onClick={onBack}
                style={{
                  padding: 'var(--space-3) var(--space-8)',
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-medium)',
                  border: '1px solid var(--gray-600)',
                  backgroundColor: 'var(--dark-elevated)',
                  color: 'var(--gray-300)',
                  cursor: 'pointer',
                  borderRadius: 'var(--radius-md)',
                  transition: 'all var(--transition-fast)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--gray-700)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--dark-elevated)';
                }}
              >
                ← Back
              </button>
            )}
            {question.choices && question.choices.map((choice) => (
              <button
                key={choice}
                onClick={() => handleChoiceSelect(choice)}
                style={{
                  padding: 'var(--space-3) var(--space-6)',
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-semibold)',
                  border: '1px solid',
                  borderColor: currentData?.[question.field] === choice ? '#fbbf24' : 'var(--gray-600)',
                  backgroundColor: currentData?.[question.field] === choice ? '#fbbf24' : 'var(--dark-elevated)',
                  color: currentData?.[question.field] === choice ? '#1e1e1e' : 'var(--gray-200)',
                  cursor: 'pointer',
                  borderRadius: 'var(--radius-md)',
                  transition: 'all var(--transition-fast)'
                }}
                onMouseEnter={(e) => {
                  if (currentData?.[question.field] !== choice) {
                    e.target.style.backgroundColor = 'var(--gray-700)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentData?.[question.field] !== choice) {
                    e.target.style.backgroundColor = 'var(--dark-elevated)';
                  }
                }}
              >
                {choice}
              </button>
            ))}
          </div>
        </div>
      );

    case 'discrepancy':
      return (
        <div className="wizard-question-container" style={{
          backgroundColor: 'rgba(239, 68, 68, 0.15)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-5)'
        }}>
          <h3 style={{
            marginBottom: 'var(--space-3)',
            fontSize: 'var(--text-lg)',
            fontWeight: 'var(--font-bold)',
            color: '#f87171'
          }}>Discrepancy Required</h3>
          <p style={{
            marginBottom: 'var(--space-5)',
            fontSize: 'var(--text-base)',
            color: 'var(--gray-200)'
          }}>{question.text}</p>
          <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'center' }}>
            {canGoBack && (
              <button
                onClick={onBack}
                style={{
                  padding: 'var(--space-3) var(--space-8)',
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-medium)',
                  border: '1px solid var(--gray-600)',
                  backgroundColor: 'var(--dark-elevated)',
                  color: 'var(--gray-300)',
                  cursor: 'pointer',
                  borderRadius: 'var(--radius-md)',
                  transition: 'all var(--transition-fast)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--gray-700)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--dark-elevated)';
                }}
              >
                ← Back
              </button>
            )}
            <button
              onClick={handleDiscrepancyCreate}
              style={{
                padding: 'var(--space-3) var(--space-8)',
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-semibold)',
                border: '1px solid #f87171',
                backgroundColor: '#f87171',
                color: 'white',
                cursor: 'pointer',
                borderRadius: 'var(--radius-md)',
                transition: 'all var(--transition-fast)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#ef4444';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#f87171';
              }}
            >
              Create Discrepancy
            </button>
          </div>
        </div>
      );

    case 'info':
      return (
        <div className="wizard-question-container" style={{
          backgroundColor: 'rgba(59, 130, 246, 0.15)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-5)'
        }}>
          <h3 style={{
            marginBottom: 'var(--space-3)',
            fontSize: 'var(--text-lg)',
            fontWeight: 'var(--font-bold)',
            color: 'var(--cta-blue)'
          }}>Information</h3>
          <p style={{
            marginBottom: 'var(--space-5)',
            fontSize: 'var(--text-base)',
            color: 'var(--gray-200)'
          }}>{question.text}</p>
          <button
            onClick={handleInfoAcknowledge}
            style={{
              padding: 'var(--space-3) var(--space-6)',
              fontSize: 'var(--text-base)',
              fontWeight: 'var(--font-semibold)',
              border: '1px solid var(--cta-blue)',
              backgroundColor: 'var(--cta-blue)',
              color: 'white',
              cursor: 'pointer',
              borderRadius: 'var(--radius-md)',
              transition: 'all var(--transition-fast)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#2563eb';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'var(--cta-blue)';
            }}
          >
            OK, Continue
          </button>
        </div>
      );

    default:
      return (
        <div className="wizard-question-container">
          <p style={{
            color: '#f87171',
            fontSize: 'var(--text-base)',
            fontWeight: 'var(--font-medium)'
          }}>Unknown question type: {question.type}</p>
        </div>
      );
  }
};

export default WizardQuestion;
